import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UnheadProvider, createHead } from '@unhead/react/client';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { propertyApi, Property, settingsApi } from './services/api';
import { getPropertyUrl } from './utils/formatters';
import { isKakaoTalkInAppBrowser, openInExternalBrowser } from './utils/browserUtils';
import HomePage from './pages/HomePage';
import ListPage from './pages/ListPage';
import MapPage from './pages/MapPage';
import LoginPage from './pages/LoginPage';
import KakaoCallbackPage from './pages/KakaoCallbackPage';
import PresalePage from './pages/PresalePage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import SideInquiryPage from './pages/SideInquiryPage';
import GeneralInquiryPage from './pages/GeneralInquiryPage';
import BusinessInquiryPage from './pages/BusinessInquiryPage';
import RegionPage from './pages/RegionPage';
import ComparePage from './pages/ComparePage';
import ProfilePage from './pages/ProfilePage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import BottomNavigation from './components/BottomNavigation';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationBell from './components/NotificationBell';
import { DesktopNav } from './design-system/components';
import SplashScreen from './components/SplashScreen';
import { captureLandingParams, initTracking, installContactClickTracking, trackPageView } from './utils/tracking';
import './App.css';

// 첫 진입 URL 의 utm·fbclid 보존 — 라우터·페이지가 URL 을 바꾸기 전에 (2026-09-22)
captureLandingParams();

// 개발 전용(디자인 카탈로그 + Legacy 시각 비교) — React.lazy 코드 스플리팅으로
// 프로덕션 번들에서 제외 (top-level import 시 dev 전용 라우트여도 번들에 포함되는 문제 해결, 2026-07-07)
const DesignSystemCatalog = React.lazy(() => import('./design-system/components/__stories__/Catalog'));
const DaeguLandingPageLegacy = React.lazy(() => import('./pages/DaeguLandingPageLegacy'));
const ListPageLegacy = React.lazy(() => import('./pages/ListPageLegacy'));
const MapPageLegacy = React.lazy(() => import('./pages/MapPageLegacy'));
const PropertyDetailPageLegacy = React.lazy(() => import('./pages/PropertyDetailPageLegacy'));

// React 19 호환 head/SEO 관리 (react-helmet-async 대체)
// 한 번만 생성하여 전 앱에서 공유.
const head = createHead();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// 검색어 정규화 함수 (띄어쓰기 무시)
const normalizeForSearch = (text: string): string => {
  return text.toLowerCase().replace(/\s+/g, '');
};

// 테마 색상 헬퍼 함수
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const adjustBrightness = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const adjust = (value: number) => {
    const adjusted = Math.round(value * (1 + percent / 100));
    return Math.max(0, Math.min(255, adjusted));
  };

  const r = adjust(rgb.r).toString(16).padStart(2, '0');
  const g = adjust(rgb.g).toString(16).padStart(2, '0');
  const b = adjust(rgb.b).toString(16).padStart(2, '0');

  return `#${r}${g}${b}`;
};

// 동적 스크립트 삽입 함수
// 관리자 스크립트를 "작성된 순서대로" 실행한다.
//  - 동적으로 넣은 외부 <script src> 는 기본이 async 라, 바로 뒤 인라인 호출(예: kakaoPixel(...))이
//    라이브러리 로드 전에 실행돼 ReferenceError 가 났다(2026-09-22 로컬 오류 화면). 외부 스크립트는
//    onload(또는 오류·5초 타임아웃)까지 기다린 뒤 다음 노드를 넣는다.
const appendInOrder = (parent: HTMLElement, html: string, tag: 'head' | 'body') => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const nodes = Array.from(container.children);
  const step = (i: number) => {
    if (i >= nodes.length) return;
    const child = nodes[i];
    if (child.tagName !== 'SCRIPT') {
      const clone = child.cloneNode(true) as Element;
      clone.setAttribute('data-dynamic-script', tag);
      parent.appendChild(clone);
      step(i + 1);
      return;
    }
    // innerHTML 로 넣은 script 는 실행되지 않으므로 새로 만든다
    const script = document.createElement('script');
    Array.from(child.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
    script.setAttribute('data-dynamic-script', tag);
    const src = child.getAttribute('src');
    if (src) {
      script.async = false;
      let done = false;
      const next = () => { if (!done) { done = true; step(i + 1); } };
      script.onload = next;
      script.onerror = next;
      window.setTimeout(next, 5000);
      parent.appendChild(script);
    } else {
      script.textContent = child.textContent;
      try {
        parent.appendChild(script);
      } catch (e) {
        console.error('[injectScripts] 관리자 스크립트 실행 오류:', e);
      }
      step(i + 1);
    }
  };
  step(0);
};

const injectScripts = (headScripts: string | null, bodyScripts: string | null) => {
  if (headScripts) {
    document.querySelectorAll('[data-dynamic-script="head"]').forEach((el) => el.remove());
    appendInOrder(document.head, headScripts, 'head');
  }
  if (bodyScripts) {
    document.querySelectorAll('[data-dynamic-script="body"]').forEach((el) => el.remove());
    appendInOrder(document.body, bodyScripts, 'body');
  }
};

// SiteSettings.theme_color 구 기본값 (v1 오렌지). v2 전환 후에는 주입 대상에서 제외.
const LEGACY_THEME_COLOR = '#ff9100';

const applyThemeColor = (primaryColor: string) => {
  const root = document.documentElement;
  const rgb = hexToRgb(primaryColor);

  // Primary 색상 변형
  const darkColor = adjustBrightness(primaryColor, -15);
  const darkerColor = adjustBrightness(primaryColor, -25);
  const lightColor = adjustBrightness(primaryColor, 60);
  const subtleColor = adjustBrightness(primaryColor, 85);
  const mutedColor = adjustBrightness(primaryColor, 70);

  // CSS 변수 설정
  root.style.setProperty('--primary-main', primaryColor);
  root.style.setProperty('--primary-dark', darkColor);
  root.style.setProperty('--primary-darker', darkerColor);
  root.style.setProperty('--primary-light', lightColor);
  root.style.setProperty('--primary-subtle', subtleColor);
  root.style.setProperty('--primary-muted', mutedColor);
  root.style.setProperty('--accent-gold', primaryColor);
  root.style.setProperty('--accent-gold-dark', darkColor);
  root.style.setProperty('--accent-gold-light', lightColor);
  root.style.setProperty('--status-upcoming', primaryColor);

  // RGB 값 (rgba에서 사용)
  if (rgb) {
    root.style.setProperty('--primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  }
};

const AppContent: React.FC = () => {
  const { user, logout, isLoading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState<Property[]>([]);
  const [suggestions, setSuggestions] = useState<Property[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMyLocation, setShowMyLocation] = useState(false);
  const [showSpecialList, setShowSpecialList] = useState(false);
  const [visibleSpecialProperties, setVisibleSpecialProperties] = useState<Property[]>([]);
  const [siteLogo, setSiteLogo] = useState<string | null>(null);
  const [showSplash, setShowSplash] = useState(() => {
    // 세션 내에서 한 번만 스플래시 표시.
    // 2026-09-26: 홈(지도)으로 들어올 때만 — 스플래시는 지도용 전체 현장 데이터를 기다리느라(최대 2초)
    //   상세·목록 등으로 바로 들어온 방문자(광고·검색 유입)는 필요 없는 대기를 했다(대표 제보: /property/2749 느림).
    if (window.location.pathname !== '/') return false;
    return !sessionStorage.getItem('splashShown');
  });
  const [isDataReady, setIsDataReady] = useState(false);  // 데이터 로딩 완료 상태
  const navigate = useNavigate();
  const location = useLocation();

  // SPA 라우트 변경 시 페이지뷰 (메타·GA4·카카오·네이버 — utils/tracking, 관리자 설정 기반).
  // 최초 진입 페이지뷰는 initTracking 이 보내므로 첫 실행은 건너뛴다(이중 전송 방지).
  // REPLACE(리다이렉트: 상세 ID→주택관리번호 URL, 404→/) 도 같은 화면이라 건너뛴다.
  const navigationType = useNavigationType();
  const isFirstRouteRef = useRef(true);
  useEffect(() => {
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return;
    }
    if (navigationType === 'REPLACE') return;
    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 전화(tel:)·카톡 상담 링크 클릭 → Contact 전환 (문서 전체 위임)
  useEffect(() => installContactClickTracking(), []);

  // 카카오톡 인앱 브라우저 감지
  const isInKakaoTalk = isKakaoTalkInAppBrowser();

  // 외부 브라우저 열기 핸들러
  const handleOpenExternalBrowser = () => {
    openInExternalBrowser();
  };

  // 스플래시 완료 핸들러
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
    sessionStorage.setItem('splashShown', 'true');
  }, []);

  // 스플래시 중에 데이터 병렬 로딩 (즉시 시작)
  useEffect(() => {
    let propertiesLoaded = false;
    let settingsLoaded = false;

    const checkDataReady = () => {
      if (propertiesLoaded && settingsLoaded) {
        setIsDataReady(true);
      }
    };

    // 현장 데이터 로딩 - 경량 map API 사용 (1초, 전체 API 22초 대비)
    const loadProperties = async () => {
      try {
        const mapData = await propertyApi.getMapData();
        const propertiesList = mapData.filter((p: Property) => p.status !== '분양완료');
        setProperties(propertiesList);
      } catch (error) {
        console.error('Failed to load properties:', error);
      } finally {
        propertiesLoaded = true;
        checkDataReady();
      }
    };

    // 사이트 설정 로딩 (로고, 테마 색상, 추적 스크립트)
    const loadSettings = async () => {
      try {
        const settings = await settingsApi.get();
        if (settings.logo) {
          setSiteLogo(settings.logo);
        }
        // 테마 색상 적용 — 단, 구 기본값(#ff9100)은 v2 오렌지(#FF6B00, index.css --primary-*)를
        // 덮어쓰므로 적용하지 않는다(v2 2026-09-21). 관리자가 다른 색을 지정한 경우만 런타임 주입.
        if (settings.theme_color && settings.theme_color.trim().toLowerCase() !== LEGACY_THEME_COLOR) {
          applyThemeColor(settings.theme_color);
        }
        // 추적 스크립트 동적 삽입 (관리자 HEAD/BODY 스크립트 + 고급 HEAD 추가 HTML)
        const headHtml = [settings.head_scripts, settings.head_extra_html].filter(Boolean).join('\n');
        injectScripts(headHtml || null, settings.body_scripts || null);
        // 광고·분석 ID 기반 로드 — 위 주입 뒤에 호출해야 같은 도구 중복 로드를 감지한다
        initTracking(settings);
      } catch (error) {
        console.error('Failed to load site settings:', error);
      } finally {
        settingsLoaded = true;
        checkDataReady();
      }
    };

    // 병렬로 즉시 로딩 시작
    loadProperties();
    loadSettings();
  }, []);

  const handleSearchInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.length >= 2) {
      try {
        // 백엔드 autocomplete API 호출 (토큰 기반 검색)
        // 예: "대명힐" → "힐스테이트 대명센트럴 2차" 매칭
        const results = await propertyApi.autocomplete(value);
        setSuggestions(results.slice(0, 5));
        setShowSuggestions(true);
      } catch (error) {
        // API 실패 시 로컬 필터링으로 폴백
        const normalizedQuery = normalizeForSearch(value);
        const filtered = properties.filter(
          (property) => {
            const normalizedTitle = normalizeForSearch(property.title);
            const normalizedAddress = normalizeForSearch(property.address);
            return normalizedTitle.includes(normalizedQuery) || normalizedAddress.includes(normalizedQuery);
          }
        ).slice(0, 5);
        setSuggestions(filtered);
        setShowSuggestions(true);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (property: Property) => {
    setSearchQuery('');
    setShowSuggestions(false);

    // 지도 페이지에서는 해당 현장 위치로 이동
    const propertyIdOrNo = property.house_manage_no || property.id;
    if (location.pathname === '/map') {
      navigate(`/map?property=${propertyIdOrNo}`);
    } else if (location.pathname.startsWith('/property/')) {
      // 상세 페이지에서 다른 상세 페이지로 이동할 때는 페이지 전환을 강제
      // React Router가 같은 라우트 패턴이라 컴포넌트를 재사용하는 문제 해결
      window.location.href = getPropertyUrl(property);
    } else {
      navigate(getPropertyUrl(property));
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // 띄어쓰기 무시하고 매칭되는 현장 찾기
      const normalizedQuery = normalizeForSearch(searchQuery);

      // 매칭되는 모든 현장 찾기
      const matchedProperties = properties.filter(p => {
        const nt = normalizeForSearch(p.title);
        const na = normalizeForSearch(p.address);
        return nt.includes(normalizedQuery) || na.includes(normalizedQuery);
      });

      if (matchedProperties.length === 1) {
        // 정확히 1개만 매칭되면 상세 페이지로 이동 (어느 페이지에서든)
        navigate(getPropertyUrl(matchedProperties[0]));
      } else if (matchedProperties.length > 1 && location.pathname === '/map') {
        // 여러 개 매칭 + 지도 페이지 → 첫 번째 결과 위치로 이동
        const matchedIdOrNo = matchedProperties[0].house_manage_no || matchedProperties[0].id;
        navigate(`/map?property=${matchedIdOrNo}`);
      } else {
        // 매칭 없거나 여러 개면 목록 페이지로
        navigate(`/list?search=${encodeURIComponent(searchQuery.trim())}`);
      }
      setSearchQuery('');
      setShowSuggestions(false);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // 로고 클릭 - 메인페이지에서는 새로고침
  const handleLogoClick = (e: React.MouseEvent) => {
    closeMobileMenu();
    if (location.pathname === '/') {
      e.preventDefault();
      window.location.reload();
    }
  };

  // 내 위치 버튼 클릭 - 지도로 이동하고 내 위치 표시
  const handleMyLocationClick = useCallback(() => {
    const isPropertyPage = location.pathname.startsWith('/property/');

    if (location.pathname === '/map') {
      // 이미 지도 페이지에 있으면 바로 위치 표시
      setShowMyLocation(true);
      setShowSpecialList(false); // 리스트 닫기
    } else if (isPropertyPage) {
      // 상세페이지에서는 완전한 페이지 리로드 필요
      // (SPA 라우팅으로는 카카오맵 SDK가 제대로 재초기화되지 않음)
      sessionStorage.setItem('showMyLocation', 'true');
      window.location.href = '/map';
    } else {
      // 다른 페이지에서는 SPA 라우팅 사용
      navigate('/map');
      setTimeout(() => {
        setShowMyLocation(true);
      }, 100);
    }
  }, [location.pathname, navigate]);

  // 내 위치 표시 후 상태 리셋
  const handleMyLocationShown = useCallback(() => {
    setShowMyLocation(false);
  }, []);

  // 리스트 토글 핸들러
  const handleListToggle = useCallback(() => {
    setShowSpecialList(prev => !prev);
  }, []);

  // 스페셜 현장 업데이트 핸들러 (MapPage에서 호출)
  const handleSpecialPropertiesUpdate = useCallback((properties: Property[]) => {
    setVisibleSpecialProperties(properties);
  }, []);

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading">로딩 중...</div>
        </div>
      </div>
    );
  }

  // 스플래시 화면 (데이터 로딩과 병렬 실행)
  if (showSplash) {
    return <SplashScreen logo={siteLogo} onComplete={handleSplashComplete} isDataReady={isDataReady} />;
  }

  // Concept A 적용 라우트 — 자체 StickyHeader 사용. 글로벌 .app-header 숨김.
  // 시안 wireframes/concept-a-warm/* 와 동일한 헤더가 페이지별로 렌더됨.
  // 그 외 라우트 (login, presales, privacy, terms, side-inquiry, /_legacy-*, /_catalog 등) 는
  // 글로벌 헤더 유지 — 자체 헤더 없는 보조 페이지에서 햄버거 + 로고 필요.
  const conceptAPaths = ['/', '/list', '/map', '/profile'];
  const isConceptAPage =
    conceptAPaths.includes(location.pathname) ||
    location.pathname.startsWith('/property/');

  return (
    <div className="App">
      {!isConceptAPage && (
      <header className="app-header">
        <div className="header-container">
          {/* Mobile hamburger menu */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="메뉴 열기"
          >
            <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          {/* Logo - 이미지만 표시 */}
          <Link to="/" className="logo" onClick={handleLogoClick}>
            {siteLogo && <img src={siteLogo} alt="부냥" className="logo-image" />}
          </Link>

          {/* Mobile notification bell - 로그인한 사용자만 */}
          {user && (
            <div className="mobile-notification">
              <NotificationBell />
            </div>
          )}

          {/* PC 상단 네비게이션 — '마이' 메뉴 (2026-05-24) */}
          <DesktopNav className="ml-auto" />
        </div>

        {/* Mobile navigation overlay */}
        <div className={`mobile-nav-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}></div>

        {/* Mobile navigation menu */}
        <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-header">
            <span className="mobile-nav-title">메뉴</span>
            <button className="mobile-nav-close" onClick={closeMobileMenu} aria-label="메뉴 닫기">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>
          <div className="mobile-nav-links">
            {/* 목록/지도 메뉴 숨김 (추후 오픈 예정) */}
            {user ? (
              <>
                <Link to="/profile" onClick={closeMobileMenu}>{user.username}님</Link>
                <button onClick={() => { logout(); closeMobileMenu(); }} className="mobile-logout-btn">
                  로그아웃
                </button>
              </>
            ) : (
              <Link to="/login" onClick={closeMobileMenu}>로그인</Link>
            )}
          </div>
        </nav>
      </header>
      )}

      {/* 카카오톡 인앱 브라우저 감지 시 외부 브라우저 안내 (로그인 페이지 제외) */}
      {isInKakaoTalk && location.pathname !== '/login' && (
        <div className="inapp-browser-banner">
          <div className="inapp-banner-content">
            <span className="inapp-banner-text">
              카카오톡에서 접속하셨네요! 더 나은 경험을 위해 외부 브라우저를 이용해주세요.
            </span>
            <button
              type="button"
              className="inapp-banner-btn"
              onClick={handleOpenExternalBrowser}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              외부 브라우저로 열기
            </button>
          </div>
        </div>
      )}

      {/* mobile: BottomNavigation(fixed, h-[64px] v2, md:hidden) 회피용 일괄 padding.
          개별 페이지가 추가로 sticky CTA 가 있다면 그 페이지 안에서 별도 spacer 처리.
          글로벌 .app-header 는 position:absolute 라 본문 위에 떠 있으므로,
          isConceptAPage(자체 헤더) 가 아닌 페이지엔 top padding 일괄 적용 (2026-05-24). */}
      <main className={`pb-[64px] md:pb-0 ${!isConceptAPage ? 'pt-16 md:pt-20' : ''}`}>
        <ErrorBoundary>
        <Routes>
          {/* 메인페이지 = 지도 (사용자 피드백 2026-05-18). (구 /landing DaeguLandingPage 는 2026-09-26 삭제) */}
          <Route path="/" element={
            <MapPage
              showMyLocation={showMyLocation}
              onMyLocationShown={handleMyLocationShown}
              onSpecialPropertiesUpdate={handleSpecialPropertiesUpdate}
            />
          } />
          <Route path="/map" element={
            <MapPage
              showMyLocation={showMyLocation}
              onMyLocationShown={handleMyLocationShown}
              onSpecialPropertiesUpdate={handleSpecialPropertiesUpdate}
            />
          } />
          {/* /landing — 2026-09-26 대표 지시로 페이지 삭제. 옛 링크는 아래 * 규칙으로 홈(/)으로, 크롤러는 nginx 301 */}
          {/* 지역 허브 페이지 (SEO 롱테일) — /region/{시}/{구} */}
          <Route path="/region/:si/:gu" element={<RegionPage />} />
          {/* 단지 비교 (개인화 페이지, noindex) — 비교함 담긴 2~4개 나란히 비교 */}
          <Route path="/compare" element={<ComparePage />} />
          {/* /list 재공개 — 대표 결정 2026-09-21 #1 (v2 5탭·GNB '분양목록'). 이전: 2026-05-22 비공개 redirect */}
          <Route path="/list" element={<ListPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/presales" element={<PresalePage />} />
          <Route path="/property/:id" element={<PropertyDetailPage />} />
          <Route path="/side-inquiry/:id" element={<SideInquiryPage />} />
          <Route path="/side-inquiry" element={<GeneralInquiryPage />} />
          <Route path="/business-inquiry" element={<BusinessInquiryPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/kakao/callback" element={<KakaoCallbackPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          {/* 디자인 카탈로그 + Legacy 비교 라우트는 개발 빌드에서만 노출.
              운영에서는 공개·크롤·구버전 가격/상태 마스킹 정책 위반을 막기 위해 라우트 자체를 제거. */}
          {process.env.NODE_ENV !== 'production' && (
            <>
              <Route path="/_catalog" element={<Suspense fallback={null}><DesignSystemCatalog /></Suspense>} />
              <Route path="/_legacy-main" element={<Suspense fallback={null}><DaeguLandingPageLegacy /></Suspense>} />
              <Route path="/_legacy-list" element={<Suspense fallback={null}><ListPageLegacy /></Suspense>} />
              <Route path="/_legacy-map" element={
                <Suspense fallback={null}>
                  <MapPageLegacy
                    showMyLocation={showMyLocation}
                    onMyLocationShown={handleMyLocationShown}
                    onSpecialPropertiesUpdate={handleSpecialPropertiesUpdate}
                  />
                </Suspense>
              } />
              <Route path="/_legacy-detail/:id" element={<Suspense fallback={null}><PropertyDetailPageLegacy /></Suspense>} />
            </>
          )}
          {/* 404 catch-all — 잘못된 URL은 홈으로 (빈 화면 방지, 2026-07-07) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </main>
      {/* 푸터 — 사용자 피드백 2026-05-19: 한 줄, 최소 높이 */}
      <footer className="app-footer-mini">
        © 2024 부냥. All rights reserved. · 상호명: 큐비컴 | 사업자등록번호: 502-30-39667
        {/* v2(2026-09-21): 모바일 '전체' 메뉴 시트가 5탭 전환으로 빠져 약관 링크를 푸터로 (PC 목업 푸터 동일) */}
        <span className="mx-2">·</span>
        <Link to="/terms">이용약관</Link>
        <span className="mx-1.5">·</span>
        <Link to="/privacy">개인정보처리방침</Link>
        <span className="mx-1.5">·</span>
        <Link to="/business-inquiry">광고·제휴 문의</Link>
      </footer>

      {/* Bottom Navigation — Concept A 톤 재설계 (2026-05-19) */}
      <BottomNavigation
        onMyLocationClick={handleMyLocationClick}
      />
    </div>
  );
};

function App() {
  return (
    <UnheadProvider head={head}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </UnheadProvider>
  );
}

export default App;
